import React from 'react';
import dayjs from 'dayjs';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';

import BorderedSection from '../../shared/borderedSection';
import { DisplayValue } from '../shared-ui';
import { DeviceInformation } from '../shared-ui/device-info';
import { PanelFrame } from '../shared-ui/panel-frame';
import DeviceLoadingSkeleton from '../shared-ui/deviceLoadingSkeleton';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';
import {
  ControllerDetailQuery,
  useControllerDetailQuery,
  useControllerDetailUpdateSubscription,
} from '../../../../types/generated-types';

type MetricValue = {
  timestamp?: number | null;
  value?: boolean | number | string | null;
  units?: string | null;
};

type ControllerDevice = NonNullable<ControllerDetailQuery['controllerById']>;

function metricDisplayValue(
  metric?: MetricValue | null,
): number | string | undefined {
  if (metric?.value === null || metric?.value === undefined) {
    return undefined;
  }
  if (typeof metric.value === 'boolean') {
    return metric.value ? 1 : 0;
  }
  return metric.value;
}

export function ControllerComponent({ deviceId }: { deviceId: string }) {
  const { data, error, loading } = useControllerDetailQuery({
    variables: { id: deviceId },
  });

  useControllerDetailUpdateSubscription({
    variables: { ids: [deviceId] },
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  const device = data?.controllerById as ControllerDevice | undefined;
  const panel = React.useMemo(() => {
    if (!device?.panel) return undefined;

    return {
      _id: device.panel._id ?? undefined,
      name: device.panel.name ?? undefined,
      displayName: device.panel.displayName ?? undefined,
      sourceId: device.panel.sourceId ?? undefined,
    };
  }, [device]);

  const infoEntries: DeviceInformation[] = React.useMemo(() => {
    if (!device) return [];

    const panelInfo: DeviceInformation = {
      Panel: device.panel?.displayName ?? device.panel?.name ?? 'Unnamed',
      'Panel ID': device.panelId ?? device.panel?._id ?? 'Unidentified',
      Source: device.source?.name ?? 'Unnamed source',
    };
    const deviceInfo: DeviceInformation = {
      'Device ID': device.deviceId ?? device._id ?? 'Unidentified',
      Name: device.name ?? device.displayName ?? 'Unnamed Controller',
      Type: device.typeDisplayName ?? 'Controller',
      Model: device.meta?.model ?? device.meta?.modelNumber ?? 'Unknown',
      Version: device.meta?.version ?? 'Unknown',
      'Last Updated': device.timestamp
        ? dayjs(device.timestamp).fromNow()
        : 'Never reported',
    };

    return [panelInfo, deviceInfo];
  }, [device]);

  if (error) {
    return <Alert severity="error">Unable to load Controller details.</Alert>;
  }

  if (loading || !device) {
    return <DeviceLoadingSkeleton />;
  }

  return panel ? (
    <PanelFrame
      iconType="mdi:controller"
      panel={panel}
      infoEntries={infoEntries}
      editingEnabled={false}
    >
      <BorderedSection
        title="Diagnostics"
        labelStyle={{ fontSize: '10px' }}
        ccStyle={{
          padding: '4px 4px 6px 4px',
          marginLeft: '4px',
          margin: '0px',
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
        }}
        style={{ width: '100%' }}
      >
        <BorderedSection
          title="Communication"
          labelStyle={{ fontSize: '10px' }}
          style={{ height: '45px', flex: '1 1 auto', margin: '0px 3px' }}
        >
          <Typography
            sx={{
              fontSize: '16px',
              lineHeight: '45px',
              textAlign: 'center',
              width: '100%',
            }}
          >
            {device.communicationState?.value === false ? 'Offline' : 'Online'}
          </Typography>
        </BorderedSection>
        <BorderedSection
          title="Active Alarms"
          labelStyle={{ fontSize: '10px' }}
          style={{ height: '45px', flex: '1 1 auto', margin: '0px 3px' }}
        >
          <DisplayValue
            value={metricDisplayValue(device.activeAlarmCount)}
            valueSize="16px"
            style={{ width: '100%', textAlign: 'center' }}
          />
        </BorderedSection>
        <BorderedSection
          title="Resets"
          labelStyle={{ fontSize: '10px' }}
          style={{ height: '45px', flex: '1 1 auto', margin: '0px 3px' }}
        >
          <DisplayValue
            value={metricDisplayValue(device.resetCount)}
            valueSize="16px"
            style={{ width: '100%', textAlign: 'center' }}
          />
        </BorderedSection>
      </BorderedSection>
    </PanelFrame>
  ) : (
    <DeviceLoadingSkeleton />
  );
}
